import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import { MdCheckCircleOutline, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { NavGraphFormValues } from '~/types/navigation-graph'
import type { IPageContainer } from '~/types/page-container'

import NavigationTargetBadge from './NavigationTargetBadge'

import PageContainersSelectionModal from '../modals/PageContainersSelectionModal'

import { getFile } from '~/api/file'
import Button from '~/components/Buttons/Button'
import PageContainerCard from '~/components/PageContainerCard'
import Score from '~/components/Score'
import { pageContainersKeys, usePageContainerDetailsQuery } from '~/queries/page-containers'
import { downloadFromUrl } from '~/utils/file'

import './styles.scss'

interface Props {
  capability?: string
  isEditable: boolean
  isRootPageContainer?: boolean
  uuid?: string
}

const PageContainerBlock = ({ capability, isEditable, isRootPageContainer, uuid }: Props): ReactElement => {
  /** Local state */

  const { setFieldValue, values } = useFormikContext<NavGraphFormValues>()

  const queryClient = useQueryClient()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: pageContainerData } = usePageContainerDetailsQuery(uuid)

  const subBlockClasses = classNames('page-container-content', { 'is-visible': !!uuid })

  const filteredNavTargets = [...new Set(pageContainerData?.navigation_target_outputs)]

  const workspaceFileId = pageContainerData?.platform_dependant_properties?.workspace_file_id

  const downloadMenuItem = workspaceFileId
    ? [
        {
          label: 'Download',
          onClick: async () => {
            const { download_url } = await getFile({ fileId: workspaceFileId })
            downloadFromUrl(download_url)
          },
        },
      ]
    : []

  const pageContainerCardMenu = (pageContainerId: string) => [
    {
      items: [
        {
          label: 'View',
          onClick: () => window.open(`${location.origin}/dynamic-screens/page-containers/${pageContainerId}`),
        },
        ...downloadMenuItem,
      ],
    },
  ]

  /** Handlers */

  const handleModalOpen = () => setIsModalOpen(true)

  const handleModalClose = () => {
    queryClient.invalidateQueries({ queryKey: pageContainersKeys.infiniteList(isModalOpen) })
    setIsModalOpen(false)
  }

  const handlePageContainerCardClick = (pageContainer: IPageContainer) => {
    if (isRootPageContainer) {
      setFieldValue('rootPageContainer', pageContainer)
      setFieldValue(
        'navigationTargetToPageContainer',
        pageContainer.navigation_target_outputs.map((targetKey) => ({
          navigation_graph_id: '',
          navigation_target_key: targetKey,
          page_container_uuid: '',
        }))
      )
    } else {
      // NOTE: find in the navigationTargetToPageContainer the affected navigation target
      const targetToUpdate = values.navigationTargetToPageContainer.find(
        (target) => target.navigation_target_key === capability
      )

      // NOTE: remove old capabilities linked to the affected navigation target
      const filteredNavigationTargetToPageContainer = targetToUpdate
        ? values.navigationTargetToPageContainer.filter(
            ({ navigation_target_key }) =>
              !filteredNavTargets.includes(navigation_target_key) ||
              navigation_target_key === targetToUpdate.navigation_target_key ||
              values.rootPageContainer?.navigation_target_outputs.includes(navigation_target_key)
          )
        : values.navigationTargetToPageContainer

      // NOTE: change its uuid to match the selected navaigation target
      if (targetToUpdate) {
        targetToUpdate.page_container_uuid = pageContainer.uuid
      }

      if (pageContainer.navigation_target_outputs) {
        const newValues = []

        const oldNavigationTargets = filteredNavigationTargetToPageContainer

        for (const oldNavigationTarget of oldNavigationTargets) {
          const target = pageContainer.navigation_target_outputs.find(
            (output) => oldNavigationTarget.navigation_target_key === output
          )

          if (!target) {
            newValues.push(oldNavigationTarget)
          }
        }

        for (const newNavTarget of pageContainer.navigation_target_outputs) {
          const target = filteredNavigationTargetToPageContainer.find(
            ({ navigation_target_key }) => newNavTarget === navigation_target_key
          )

          if (target) {
            newValues.push(target)
          } else {
            const formattedNavTarget = {
              navigation_graph_id: '',
              navigation_target_key: newNavTarget,
              page_container_uuid: '',
            }

            newValues.push(formattedNavTarget)
          }
        }

        setFieldValue('navigationTargetToPageContainer', newValues)
      } else {
        setFieldValue('navigationTargetToPageContainer', filteredNavigationTargetToPageContainer)
      }
    }

    setIsModalOpen(false)
  }

  const isNavTargetSelected = (navTargetValue: string): boolean => {
    const navTarget = values.navigationTargetToPageContainer.find(
      (target) => target.navigation_target_key === navTargetValue
    )

    return !!navTarget && navTarget.page_container_uuid !== ''
  }

  const filteredTargets = values.navigationTargetToPageContainer.filter(
    ({ navigation_target_key }) => pageContainerData?.navigation_target_outputs?.includes(navigation_target_key)
  )

  const completedCount = filteredTargets.filter((target) => target.page_container_uuid !== '').length

  return (
    <>
      <div className="page-container-block-component">
        <div className="page-container-info">
          <div className="infos-wrapper">
            <div className="page-container-title">
              {isRootPageContainer
                ? 'Root page container'
                : `Affected navigation target "${capability}" to a page container`}
            </div>
            {isEditable && (
              <Button onClick={handleModalOpen} outlined={!!uuid} size="small" type="button">
                {uuid ? 'Change' : 'Choose'}
              </Button>
            )}
            {pageContainerData?.navigation_target_outputs &&
            completedCount === pageContainerData?.navigation_target_outputs.length ? (
              <MdCheckCircleOutline className="setup-confirmed-icon" color="#34c396" size={22} />
            ) : (
              <Score
                isPercentage={false}
                total={pageContainerData?.navigation_target_outputs?.length || 0}
                type="completion"
                value={completedCount}
              />
            )}
          </div>

          {pageContainerData ? !!uuid ? <MdKeyboardArrowUp size={16} /> : <MdKeyboardArrowDown size={16} /> : null}
        </div>
        {pageContainerData ? (
          <div className={subBlockClasses}>
            <PageContainerCard menu={pageContainerCardMenu(pageContainerData.uuid)} pageContainer={pageContainerData} />
            <div className="navigation-targets-container">
              {filteredNavTargets ? (
                <>
                  <div>{pageContainerData.title}</div>
                  <div className="title">Navigation targets</div>
                  <div className="navigation-target-badges-container">
                    {filteredNavTargets.map((navigationTarget, index) => (
                      <NavigationTargetBadge
                        isCurrentPageContainerSelected={isNavTargetSelected(navigationTarget)}
                        key={`${pageContainerData.uuid}-${navigationTarget}-${index}`}
                        value={navigationTarget}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="targets">No navigation targets</div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <PageContainersSelectionModal
        isOpen={isModalOpen}
        onCloseModal={handleModalClose}
        onPageContainerCardClick={handlePageContainerCardClick}
      />
    </>
  )
}

export default PageContainerBlock
